// 連續捲動：以視窗捲動做虛擬化，以「列」為單位只繪製看得到的範圍（清單再長，DOM 節點數也固定）。
// 海報牆一列有多款（欄數依可用寬度），清單版面一列一款。
// 目前所在的段落標題以 sticky 固定在頂部列下方；桌面版標題放在左欄（段落標題列本身只佔一小段間距，
// 標題內容畫在左欄，右側的第一列與它對齊），平板與手機放在內容上方。
import { defaultRangeExtractor, type Range, useWindowVirtualizer } from '@tanstack/react-virtual';
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { ListEntry } from '../../filters/catalog.ts';
import { columnsFor, toRows } from '../../filters/rows.ts';
import type { LayoutKey } from '../../storage/prefs.ts';
import { BREAKPOINTS, useMediaQuery } from '../../ui/useMediaQuery.ts';
import { GameCard } from './GameCard.tsx';
import { GroupHeader } from './GroupHeader.tsx';
import { PosterCard } from './PosterCard.tsx';

/** 預估高度（實際高度由量測修正）：桌面的段落標題列只是段落間距 */
const ESTIMATE = { header: 76, headerWide: 36, card: 132, cardPhone: 120 };
const WIDE = '(min-width: 1024px)';
/** 桌面左欄（段落標題）寬度＋間距，與 CSS 的 --tl-col＋--tl-gap 一致 */
const TIMELINE_INSET = 168 + 28;
/** 海報列的高度：封面 3:4＋標題兩行 */
const posterRowHeight = (colWidth: number) => Math.round(colWidth * (4 / 3) + 76);

interface Props {
  entries: ListEntry[];
  layout: LayoutKey;
  currentYear: number;
  /** 使用者時區的今天（卡片上的倒數） */
  today: number;
  /** 頂部 sticky 列的高度：被它蓋住的列不算「看得到」，分組標題才會對應到實際看到的第一款遊戲 */
  stickyOffset: number;
  onOpen: (id: string) => void;
}

export function GameList({ entries, layout, currentYear, today, stickyOffset, onOpen }: Props) {
  const wide = useMediaQuery(WIDE);
  const phone = useMediaQuery(BREAKPOINTS.phone);
  const listRef = useRef<HTMLDivElement>(null);
  const [scrollMargin, setScrollMargin] = useState(0);
  const [listWidth, setListWidth] = useState(0);

  // 清單在頁面中的位置會隨上方內容（輪播、篩選面板）改變；寬度改變時重新計算欄數
  useLayoutEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const update = () => {
      setScrollMargin(el.getBoundingClientRect().top + window.scrollY - stickyOffset);
      setListWidth(el.clientWidth);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(document.body);
    return () => ro.disconnect();
  }, [stickyOffset]);

  // 有段落標題時，桌面的遊戲在左欄右側；不分段（依期待度、名稱排序）時用滿整個寬度
  const grouped = entries[0]?.kind === 'header';
  const trackWidth = Math.max(0, listWidth - (wide && grouped ? TIMELINE_INSET : 0));
  const columns = columnsFor(trackWidth, layout, window.innerWidth);
  const rows = useMemo(() => toRows(entries, columns), [entries, columns]);
  const colWidth = trackWidth ? (trackWidth - (columns - 1) * 18) / columns : 160;

  const stickyIndexes = useMemo(() => rows.flatMap((r, i) => (r.kind === 'header' ? [i] : [])), [rows]);
  const activeSticky = useRef(0);

  const rangeExtractor = useCallback(
    (range: Range) => {
      activeSticky.current = [...stickyIndexes].reverse().find((i) => range.startIndex >= i) ?? -1;
      const indexes = defaultRangeExtractor(range);
      if (activeSticky.current >= 0 && !indexes.includes(activeSticky.current)) indexes.unshift(activeSticky.current);
      return indexes;
    },
    [stickyIndexes],
  );

  const virtualizer = useWindowVirtualizer({
    count: rows.length,
    estimateSize: (i) => {
      if (rows[i]?.kind === 'header') return wide ? ESTIMATE.headerWide : ESTIMATE.header;
      if (layout === 'list') return phone ? ESTIMATE.cardPhone : ESTIMATE.card;
      return posterRowHeight(colWidth);
    },
    getItemKey: (i) => rows[i]?.key ?? i,
    // 海報列很高：上下各多繪製 2 列就足夠平順
    overscan: layout === 'grid' ? 2 : 6,
    scrollMargin,
    rangeExtractor,
  });

  return (
    <div
      ref={listRef}
      className={['vlist', layout === 'grid' && 'vlist-grid', !grouped && 'vlist-flat'].filter(Boolean).join(' ')}
      style={{ height: virtualizer.getTotalSize() }}
    >
      {virtualizer.getVirtualItems().map((v) => {
        const row = rows[v.index];
        if (!row) return null;
        const isActiveSticky = row.kind === 'header' && v.index === activeSticky.current;
        const className =
          row.kind === 'row' ? 'vrow vrow-game' : isActiveSticky ? 'vrow vrow-header vrow-sticky' : 'vrow vrow-header';
        return (
          <div
            key={v.key}
            data-index={v.index}
            data-group={row.kind === 'header' ? row.header.key : row.section}
            ref={virtualizer.measureElement}
            className={className}
            style={isActiveSticky ? undefined : { transform: `translateY(${v.start - scrollMargin}px)` }}
          >
            {row.kind === 'header' ? (
              <GroupHeader header={row.header} />
            ) : layout === 'grid' ? (
              <div className="poster-row" style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
                {row.items.map((item) => (
                  <PosterCard key={item.row.id} item={item} today={today} onOpen={onOpen} />
                ))}
              </div>
            ) : (
              row.items.map((item) => (
                <GameCard key={item.row.id} item={item} currentYear={currentYear} today={today} onOpen={onOpen} />
              ))
            )}
          </div>
        );
      })}
    </div>
  );
}
